import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  IconButton,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import { DateRangePicker } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { useBrand } from "../context/BrandContext";
import { getPresenceReport } from "./funtionsBD/Mainservice";
import { DEMO_PLAYERS } from "../tour/tourDemoFixtures";
import { isPingfloTourRunning } from "../tour/tourTarget";
import "./PresenceReportDialog.css";

const PRESETS = [
  "lastWeek",
  "last2Weeks",
  "lastMonth",
  "last3Months",
  "lastSeason",
  "custom",
];

const MARK_TONES = ["pine", "ink", "clay", "slate", "lagoon"];

function getSeasonRange(ref = moment()) {
  const m = moment(ref);
  if (m.month() >= 8) {
    return {
      start: m.clone().month(8).date(1).startOf("day"),
      end: m.clone().add(1, "year").month(7).date(31).endOf("day"),
    };
  }
  return {
    start: m.clone().subtract(1, "year").month(8).date(1).startOf("day"),
    end: m.clone().month(7).date(31).endOf("day"),
  };
}

function rangeForPreset(preset) {
  const today = moment().endOf("day");
  switch (preset) {
    case "lastWeek":
      return {
        start: moment().subtract(7, "days").startOf("day"),
        end: today,
      };
    case "last2Weeks":
      return {
        start: moment().subtract(14, "days").startOf("day"),
        end: today,
      };
    case "lastMonth":
      return {
        start: moment().subtract(1, "month").startOf("day"),
        end: today,
      };
    case "last3Months":
      return {
        start: moment().subtract(3, "months").startOf("day"),
        end: today,
      };
    case "lastSeason": {
      const season = getSeasonRange();
      return {
        start: season.start,
        end: moment.min(season.end, today),
      };
    }
    default:
      return {
        start: moment().subtract(1, "month").startOf("day"),
        end: today,
      };
  }
}

function presetLabelKey(preset) {
  const map = {
    lastWeek: "presenceReportLastWeek",
    last2Weeks: "presenceReportLast2Weeks",
    lastMonth: "presenceReportLastMonth",
    last3Months: "presenceReportLast3Months",
    lastSeason: "presenceReportLastSeason",
    custom: "presenceReportCustom",
  };
  return map[preset] || "presenceReportCustom";
}

function formatHours(hours) {
  const n = Number(hours) || 0;
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

function playerInitials(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

function markTone(name) {
  const source = String(name || "");
  let hash = 0;
  for (let i = 0; i < source.length; i += 1) {
    hash = (hash * 33 + source.charCodeAt(i)) >>> 0;
  }
  return MARK_TONES[hash % MARK_TONES.length];
}

function laneTone(rank) {
  if (rank === 1) return "gold";
  if (rank === 2) return "silver";
  if (rank === 3) return "bronze";
  return "lane";
}

function comparePlayers(a, b, key, dir) {
  const factor = dir === "desc" ? -1 : 1;
  if (key === "name") {
    return factor * String(a.name || "").localeCompare(String(b.name || ""), undefined, {
      sensitivity: "base",
    });
  }
  const left = Number(a[key]) || 0;
  const right = Number(b[key]) || 0;
  if (left === right) {
    return String(a.name || "").localeCompare(String(b.name || ""), undefined, {
      sensitivity: "base",
    });
  }
  return factor * (left - right);
}

export default function PresenceReportDialog({
  open,
  onClose,
  clubId,
  clubName,
}) {
  const { t } = useTranslation();
  const { brand } = useBrand();
  const brandColor = brand?.color_primary || "#0877BC";
  const isMobile = useMediaQuery("(max-width:720px)");

  const [preset, setPreset] = useState("lastMonth");
  const [range, setRange] = useState(() => rangeForPreset("lastMonth"));
  const [customRange, setCustomRange] = useState(() => {
    const r = rangeForPreset("lastMonth");
    return [
      {
        startDate: r.start.toDate(),
        endDate: r.end.toDate(),
        key: "selection",
      },
    ];
  });
  const [players, setPlayers] = useState([]);
  const [reportSummary, setReportSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState({ key: "rank", dir: "asc" });

  const title = useMemo(() => {
    if (clubName) return `${t("presenceReport")} — ${clubName}`;
    return t("presenceReport");
  }, [clubName, t]);

  const displayPlayers = useMemo(() => {
    if (players.length) return players;
    if (!isPingfloTourRunning()) return [];
    return DEMO_PLAYERS.map((p) => ({
      player_id: p.id,
      name: p.name,
      sessions: p.sessions,
      exercises: p.exercises,
      hours: p.hours,
    }));
  }, [players]);

  const rankedPlayers = useMemo(
    () => displayPlayers.map((player, index) => ({ ...player, rank: index + 1 })),
    [displayPlayers]
  );

  const boardStats = useMemo(() => {
    const playerHours = rankedPlayers.reduce(
      (sum, player) => sum + (Number(player.hours) || 0),
      0
    );
    const maxHours = Math.max(0, ...rankedPlayers.map((player) => Number(player.hours) || 0));
    // Prefer club unique sessions from API — never sum per-player session counts.
    const groupSessions =
      reportSummary?.group_sessions != null
        ? Number(reportSummary.group_sessions) || 0
        : rankedPlayers.reduce((sum, p) => sum + (Number(p.group_sessions) || 0), 0);
    const individualSessions =
      reportSummary?.individual_sessions != null
        ? Number(reportSummary.individual_sessions) || 0
        : rankedPlayers.reduce((sum, p) => sum + (Number(p.individual_sessions) || 0), 0);
    const uniqueSessions =
      reportSummary?.unique_sessions != null
        ? Number(reportSummary.unique_sessions) || 0
        : groupSessions + individualSessions;
    const hours =
      reportSummary?.session_hours != null
        ? Number(reportSummary.session_hours) || 0
        : playerHours;
    const groupHours =
      reportSummary?.group_session_hours != null
        ? Number(reportSummary.group_session_hours) || 0
        : hours;
    const individualHours =
      reportSummary?.individual_session_hours != null
        ? Number(reportSummary.individual_session_hours) || 0
        : 0;
    return {
      count: rankedPlayers.length,
      hours,
      groupHours,
      individualHours,
      sessions: uniqueSessions,
      groupSessions,
      individualSessions,
      maxHours: maxHours || 1,
    };
  }, [rankedPlayers, reportSummary]);

  const visiblePlayers = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const filtered = needle
      ? rankedPlayers.filter((player) => String(player.name || "").toLowerCase().includes(needle))
      : rankedPlayers;
    return [...filtered].sort((a, b) => comparePlayers(a, b, sort.key, sort.dir));
  }, [rankedPlayers, query, sort]);

  const fetchReport = useCallback(
    async (start, end) => {
      if (!clubId || !open) return;
      setLoading(true);
      setError(null);
      try {
        const response = await getPresenceReport({
          club_id: clubId,
          start_date: moment(start).format("YYYY-MM-DD"),
          end_date: moment(end).format("YYYY-MM-DD"),
        });
        setPlayers(response.data?.data?.players || []);
        setReportSummary(response.data?.data?.summary || null);
      } catch (err) {
        setPlayers([]);
        setReportSummary(null);
        setError(err?.message || t("presenceReportError"));
      } finally {
        setLoading(false);
      }
    },
    [clubId, open, t]
  );

  useEffect(() => {
    if (!open) return;
    const initial = rangeForPreset("lastMonth");
    setPreset("lastMonth");
    setRange(initial);
    setQuery("");
    setSort({ key: "rank", dir: "asc" });
    setCustomRange([
      {
        startDate: initial.start.toDate(),
        endDate: initial.end.toDate(),
        key: "selection",
      },
    ]);
    fetchReport(initial.start, initial.end);
  }, [open, clubId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePreset = (next) => {
    setPreset(next);
    if (next === "custom") return;
    const nextRange = rangeForPreset(next);
    setRange(nextRange);
    setQuery("");
    setCustomRange([
      {
        startDate: nextRange.start.toDate(),
        endDate: nextRange.end.toDate(),
        key: "selection",
      },
    ]);
    fetchReport(nextRange.start, nextRange.end);
  };

  const handleApplyCustom = () => {
    const selection = customRange[0];
    if (!selection?.startDate || !selection?.endDate) return;
    const nextRange = {
      start: moment(selection.startDate).startOf("day"),
      end: moment(selection.endDate).endOf("day"),
    };
    setRange(nextRange);
    fetchReport(nextRange.start, nextRange.end);
  };

  const handleSort = (key) => {
    setSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { key, dir: key === "name" || key === "rank" ? "asc" : "desc" }
    );
  };

  const sortAria = (key) => {
    if (sort.key !== key) return "none";
    return sort.dir === "asc" ? "ascending" : "descending";
  };

  const renderSortHeader = (key, label) => (
    <th aria-sort={sortAria(key)}>
      <button
        type="button"
        className={`presence-report-dialog__sort${sort.key === key ? " is-active" : ""}`}
        onClick={() => handleSort(key)}
      >
        {label}
        <span className={`presence-report-dialog__caret is-${sort.key === key ? sort.dir : "idle"}`} aria-hidden />
      </button>
    </th>
  );

  const showBoard = Boolean(displayPlayers.length);
  const showEmptySearch = showBoard && visiblePlayers.length === 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
      className="presence-report-dialog"
      PaperProps={{
        className: "presence-report-dialog__paper",
        "data-tour-id": "admin-presence-board",
      }}
    >
      <DialogTitle className="presence-report-dialog__title">
        <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={1}>
          <Box>
            <Typography variant="h6" component="div">
              {title}
            </Typography>
            <Typography variant="body2" className="presence-report-dialog__subtitle">
              {t("presenceReportSubtitle")}
            </Typography>
            <Typography variant="caption" className="presence-report-dialog__range-label">
              {t("presenceReportFrom")} {range.start.format("DD/MM/YYYY")}{" "}
              {t("presenceReportTo")} {range.end.format("DD/MM/YYYY")}
            </Typography>
          </Box>
          <IconButton onClick={onClose} aria-label="close" size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent className="presence-report-dialog__content" data-tour-id="admin-presence-board">
        <Box className="presence-report-dialog__presets">
          {PRESETS.map((key) => (
            <Chip
              key={key}
              label={t(presetLabelKey(key))}
              onClick={() => handlePreset(key)}
              color={preset === key ? "primary" : "default"}
              variant={preset === key ? "filled" : "outlined"}
              className={`presence-report-dialog__preset-chip${
                preset === key ? " presence-report-dialog__preset-chip--selected" : ""
              }`}
            />
          ))}
        </Box>

        {preset === "custom" && (
          <Box className="presence-report-dialog__calendar">
            <DateRangePicker
              ranges={customRange}
              onChange={(item) => setCustomRange([item.selection])}
              moveRangeOnFirstSelection={false}
              months={1}
              direction="horizontal"
              rangeColors={[brandColor]}
            />
            <Button
              variant="contained"
              onClick={handleApplyCustom}
              className="presence-report-dialog__apply"
            >
              {t("presenceReportApply")}
            </Button>
          </Box>
        )}

        {loading && !displayPlayers.length ? (
          <div className="presence-report-dialog__board" aria-busy="true" aria-live="polite">
            <div className="presence-report-dialog__table-wrap">
              <table className="presence-report-dialog__table presence-report-dialog__table--skeleton">
                <tbody>
                  {Array.from({ length: 6 }).map((_, index) => (
                    <tr key={`skel-${index}`}>
                      <td colSpan={5}>
                        <span className="presence-report-dialog__skel" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="presence-report-dialog__skel-caption">{t("presenceReportLoading")}</p>
          </div>
        ) : error && !displayPlayers.length ? (
          <Box className="presence-report-dialog__state presence-report-dialog__state--error">
            <Typography variant="body2">{error}</Typography>
          </Box>
        ) : displayPlayers.length === 0 ? (
          <Box className="presence-report-dialog__state">
            <Typography variant="body2">{t("presenceReportEmpty")}</Typography>
          </Box>
        ) : (
          <div className="presence-report-dialog__board">
            <div className="presence-report-dialog__toolbar">
              <ul className="presence-report-dialog__summary">
                <li>
                  <strong>{boardStats.count}</strong>
                  <span>{t("presenceReportPlayers", "Players")}</span>
                </li>
                <li title={t("presenceReportHoursHint")}>
                  <strong>{formatHours(boardStats.hours)}</strong>
                  <span>{t("presenceReportHours")}</span>
                </li>
                <li title={t("presenceReportGroupSessionsHint")}>
                  <strong>{boardStats.groupSessions}</strong>
                  <span>{t("presenceReportGroupSessions")}</span>
                </li>
                <li title={t("presenceReportIndividualSessionsHint")}>
                  <strong>{boardStats.individualSessions}</strong>
                  <span>{t("presenceReportIndividualSessions")}</span>
                </li>
              </ul>
              <label className="presence-report-dialog__search">
                <SearchRoundedIcon fontSize="small" aria-hidden />
                <span className="sr-only">{t("presenceReportSearch", "Search players")}</span>
                <input
                  type="search"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder={t("presenceReportSearch", "Search players")}
                  autoComplete="off"
                />
              </label>
            </div>

            {showEmptySearch ? (
              <Box className="presence-report-dialog__state">
                <Typography variant="body2">
                  {t("presenceReportNoMatch", "No players match that name")}
                </Typography>
              </Box>
            ) : (
              <>
                {visiblePlayers.length > 0 ? (
                  <div
                    className="presence-report-dialog__chart"
                    data-tour-id="admin-presence-hours-chart"
                    aria-label={t("presenceReportHours")}
                  >
                    {visiblePlayers.slice(0, 8).map((player) => {
                      const hours = Number(player.hours) || 0;
                      const width = Math.max(6, Math.round((hours / boardStats.maxHours) * 100));
                      return (
                        <div key={`chart-${player.player_id}`} className="presence-report-dialog__chart-row">
                          <span className="presence-report-dialog__chart-name">{player.name}</span>
                          <div className="presence-report-dialog__chart-track" aria-hidden>
                            <span
                              className="presence-report-dialog__chart-fill"
                              style={{ width: `${width}%` }}
                            />
                          </div>
                          <span className="presence-report-dialog__chart-val">
                            {formatHours(hours)}h
                          </span>
                        </div>
                      );
                    })}
                  </div>
                ) : null}
              <div className="presence-report-dialog__table-wrap">
                <table className="presence-report-dialog__table">
                  <thead>
                    <tr>
                      {renderSortHeader("rank", t("presenceReportRank"))}
                      {renderSortHeader("name", t("presenceReportPlayer"))}
                      {renderSortHeader("group_sessions", t("presenceReportGroupSessions"))}
                      {renderSortHeader("individual_sessions", t("presenceReportIndividualSessions"))}
                      {renderSortHeader("exercises", t("presenceReportExercises"))}
                      {renderSortHeader("hours", t("presenceReportHours"))}
                    </tr>
                  </thead>
                  <tbody>
                    {visiblePlayers.map((player) => {
                      const hours = Number(player.hours) || 0;
                      const meter = Math.max(6, Math.round((hours / boardStats.maxHours) * 100));
                      return (
                        <tr
                          key={player.player_id}
                          className={player.rank <= 3 ? `is-podium is-podium-${player.rank}` : undefined}
                        >
                          <td data-label={t("presenceReportRank")} className="presence-report-dialog__rank">
                            <span className={`presence-report-dialog__lane presence-report-dialog__lane--${laneTone(player.rank)}`}>
                              {player.rank}
                            </span>
                          </td>
                          <td data-label={t("presenceReportPlayer")}>
                            <div className="presence-report-dialog__who">
                              <span
                                className={`presence-report-dialog__mark presence-report-dialog__mark--${markTone(player.name)}`}
                                aria-hidden
                              >
                                {playerInitials(player.name)}
                              </span>
                              <span className="presence-report-dialog__name">{player.name}</span>
                            </div>
                          </td>
                          <td data-label={t("presenceReportGroupSessions")}>
                            <span className="presence-report-dialog__stat">
                              {Number(player.group_sessions) || 0}
                            </span>
                          </td>
                          <td data-label={t("presenceReportIndividualSessions")}>
                            <span className="presence-report-dialog__stat">
                              {Number(player.individual_sessions) || 0}
                            </span>
                          </td>
                          <td data-label={t("presenceReportExercises")}>
                            <span className="presence-report-dialog__stat">{player.exercises}</span>
                          </td>
                          <td data-label={t("presenceReportHours")} className="presence-report-dialog__hours">
                            <div
                              className="presence-report-dialog__meter"
                              style={{ "--meter": `${meter}%` }}
                              aria-hidden
                            >
                              <span className="presence-report-dialog__meter-fill" />
                            </div>
                            <span className="presence-report-dialog__hours-val">
                              {formatHours(hours)}
                              <small>h</small>
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              </>
            )}
          </div>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>{t("close") || "Close"}</Button>
      </DialogActions>
    </Dialog>
  );
}
